import { get, post } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  JobFilterParamsSchema,
  type Job,
  type JobFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  type JobApplication
} from '@/lib/api/types'

export class JobService {
  /**
   * Filter jobs with pagination (Public endpoint)
   * POST /jobs/public/filter?size={size}&page={page}
   */
  async filterJobs(
    params: PaginationParams = { page: 1, size: 10 },
    filters: JobFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Job>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = JobFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Job>>>(
      `jobs/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search jobs (Public endpoint)
   * GET /jobs/public/search?searchBy={searchTerm}&size={size}&page={page}
   */
  async searchJobs(
    searchTerm: string,
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<Job>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      searchBy: searchTerm,
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<Job>>>(
      `jobs/public/search?${query}`
    )
  }

  /**
   * Get job by ID (Public endpoint)
   * GET /jobs/public/{jobId}
   */
  async getJobById(jobId: string): Promise<ApiResponse<Job>> {
    return await get<ApiResponse<Job>>(
      `jobs/public/${jobId}`
    )
  }

  /**
   * Get job types (Public endpoint)
   * GET /jobs/public/job-types
   */
  async getJobTypes(): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'jobs/public/job-types'
    )
  }

  /**
   * Increment job view count (Public endpoint)
   * PUT /jobs/public/{jobId}/increment-view
   */
  async incrementJobView(jobId: string): Promise<ApiResponse<null>> {
    const { put } = await import('../client')
    return await put<ApiResponse<null>>(
      `jobs/public/${jobId}/increment-view`,
      {}
    )
  }

  /**
   * Apply to a job (Public endpoint)
   * POST /jobs/public/apply
   * Accepts application data including jobId, applicant info, and uploaded files
   */
  async applyToJob(applicationData: JobApplication): Promise<ApiResponse<null>> {
    return await post<ApiResponse<null>>(
      'job-applications/public',
      applicationData
    )
  }
}

// Export singleton instance
export const jobService = new JobService()

// Export default
export default jobService

